import * as React from 'react';
import styled from 'styled-components';

import useFocus from 'src/CustomHooks/useFocus';
import useProgressBar from 'src/CustomHooks/useProgressBar';
import ISubmission from 'src/ServerEntities/Submission';
import AttributionContainer from 'src/SharedComponents/AttributionContainer';
import Button from 'src/SharedComponents/Button';
import ButtonContainer from 'src/SharedComponents/ButtonContainer';
import ContentWrapper from 'src/SharedComponents/ContentWrapper';
import ErrorToast from 'src/SharedComponents/ErrorToast';
import LoadingIndicator from 'src/SharedComponents/LoadingIndicator';
import ProgressBar from 'src/SharedComponents/ProgressBar';
import QuestionList from 'src/SharedComponents/QuestionList';

import { AxiosError } from 'axios';
import { getAnswers, saveAnswer, submitForm } from 'src/service';
import { SessionContext } from 'src/Views/SessionContext';
import { PageAnswered, PageCurrent, PageList, PageNotAnswered } from './ReqolFunctions';

import AlternateMultipleChoiceQuestion from 'src/SharedComponents/AlternateMultipleChoiceQuestion';

const Introduction = styled.p`
    font-size: 1.1rem;
    margin: 6px 12px;
`;

const ToastButtonContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
`;

const IncompleteToast = styled.div`
    position: fixed;
    bottom: 12px;
    left: -300px;
    border-radius: 16px;
    padding: 12px;
    color: white;
    border: 1px solid white;
    background-color: #005eb8;
    font-size: 1rem;
    transition: left 500ms;
    visibility: hidden;
    margin-right: 24px;
    &.sliding {
        left: 24px;
        visibility: visible;
    }
`;

const answers = ["None of the time", "Only occasionally", "Sometimes", "Often", "Most or all of the time"];
const questions = [
    "I found it difficult to get started with everyday tasks",
    "I felt able to trust others",
    "I felt unable to cope",
    "I could do the things I wanted to do",
    "I felt happy",
    "I thought my life was not worth living",
    "I enjoyed what I did",
    "I felt hopeful about my future",
    "I felt lonely",
    "I felt confident in myself"
];

const healthAnswers = ["No problems", "Slight problems", "Moderate problems", "Severe problems", "Very severe problems"];
const QUESTION_IDS = [83, 84, 85, 86, 87, 88, 89, 90, 91, 92, 93, 105];

interface IProps {
    token: string
};

const ReqolPage = (props: IProps) => {
    const [loading, setLoading] = React.useState(false);
    const [incomplete, setIncomplete] = React.useState(false);
    const { htmlElRef, setFocus} = useFocus<HTMLUListElement>();
    const [error, setError] = React.useState("");
    const { dispatch, state } = React.useContext(SessionContext);
    const answered = state.reqolAnswers;
    const webToken = state.webToken;
    const currentPage = state.page?.page ? state.page?.page - 1 : 0;
    const { numberAnswered, updateAllAnswered, ERROR_SAVING_MESSAGE } = useProgressBar();
    const questionRefs = QUESTION_IDS.map(questionId => React.useRef(null as unknown as HTMLLIElement));

    const checkUnauthorised = (serverError: AxiosError) => {
        if (serverError.response && serverError.response.data.code === 401) {
            dispatch({ type: "clearWebToken", payload: undefined });
        }
    }

    const handleError = (serverError: AxiosError, questionId: number) => {
        checkUnauthorised(serverError);
        setError(ERROR_SAVING_MESSAGE)
        answered[currentPage] = -1;
    };

    const handleSubmissionError = (serverError: AxiosError) => {
        checkUnauthorised(serverError);
        setError(ERROR_SAVING_MESSAGE)
    };

    const onAnswerChange = (questionId: number, answer: string, itemIndex?: number) => {
        setError("");
        setIncomplete(false);
        saveAnswer(questionId, answer, state.webToken, () => {
            answered[currentPage] = itemIndex !== undefined ? itemIndex : -1;
            dispatch({ type: "setReqolAnswers", payload: answered });
        }, handleError)
    };

    const goToNextPageIfComplete = () => {
        setLoading(true);
        submitForm(state.webToken, () => {
            dispatch({ type: "setPage", payload: { formId: 3, page: 13 } });
        }, handleSubmissionError);
    };

    const goToNextPage = () => {
        if (checkEnoughAnswered() && currentPage === 10) {
            goToNextPageIfComplete();
        }
        if (currentPage + 2 <= answered.length) {
            dispatch({ type: "setPage", payload: { formId: 3, page: currentPage + 2 } });
        }
    };

    const goToPreviousPage = () => {
        if (currentPage > 0) {
            dispatch({ type: "setPage", payload: { formId: 3, page: currentPage } });
        }
    };

    /**
     * Check if enough questions have been answered.
     * At least 9 of the mental health questions must be answered.
     * The physical health question is optional.
     * @returns true if 9 of the mental health questions have been answered.
     */
    const checkEnoughAnswered = () => {
        const answeredNumber = answered.filter(a => a !== -1).length;
        if (answeredNumber >= 10) {
            return true;
        }
        if (answeredNumber < 9) {
            return false;
        }
        return answered[10] === -1;
    };

    const cancelSubmit = () => {
        setIncomplete(false);
    };

    const moveToPage = (index: number) => (e: any) => {
        dispatch({ type: "setPage", payload: { formId: 3, page: index } });
    };

    const keyMoveToPage = (index: number) => (e: React.KeyboardEvent) => {
        if (e.key === " ") {
            e.stopPropagation();
            e.preventDefault();
            dispatch({ type: "setPage", payload: { formId: 3, page: index } });
        }
    };

    const pageToItem = (current: number) => (answer: number, index: number) => {
        const pageNumber = index + 1;
        if (index === current) {
            return <PageCurrent key={`page-id-${index}`} onKeyDown={keyMoveToPage(pageNumber)} href="javascript:;" onClick={moveToPage(pageNumber)}>{pageNumber}</PageCurrent>;
        }
        if (answer > -1) {
            return <PageAnswered key={`page-id-${index}`} onKeyDown={keyMoveToPage(pageNumber)} href="javascript:;" onClick={moveToPage(pageNumber)}>{pageNumber}</PageAnswered>;
        }
        return <PageNotAnswered key={`page-id-${index}`} onKeyDown={keyMoveToPage(pageNumber)} href="javascript:;" onClick={moveToPage(pageNumber)}>{pageNumber}</PageNotAnswered>;
    };

    const onDisabledClick = () => {
        const firstUnansweredIndex = answered.indexOf(-1);
        if (firstUnansweredIndex > -1) {
            moveToPage(firstUnansweredIndex + 1)("");
            setFocus();
            setError("Please answer all questions");
        }
    };

    const onSubmit = () => {
        if (answered.filter(a => a === -1).length) {
            setIncomplete(true);
        } else {
            goToNextPageIfComplete();
        }
    };

    React.useEffect(() => {
        if (webToken) {
            getAnswers(props.token, webToken, (submissionResponse: ISubmission) => {
                const questionAnswers = submissionResponse.answers.map(a => {
                    return {
                        isAnswered: true,
                        itemIndex: answers.indexOf(a.answer),
                        questionId: a.questionId
                    };
                });
                submissionResponse.answers.forEach(a => {
                    const questionIndex = questions.indexOf(a.question);
                    answered[questionIndex] = answers.indexOf(a.answer);
                });
                updateAllAnswered(questionAnswers);
                dispatch({ type: "setReqolAnswers", payload: answered });
            }, (aError: AxiosError) => {
                setError("There was an error loading previous answers. Please fill in all questions.");
            });
        }
    }, [webToken]);

    React.useEffect(() => {
        setFocus();
    }, [currentPage]);

    return <ContentWrapper>
        <Introduction>
            Please tick one box that best describes your thoughts, feelings and activities over the <strong>last week.</strong>
        </Introduction>
        <PageList>
            {answered.map(pageToItem(currentPage))}
        </PageList >
        {currentPage < 10 && <QuestionList ref={htmlElRef} tabIndex={0}>
            <AlternateMultipleChoiceQuestion checkedItem={answered[currentPage]} refProp={questionRefs[currentPage]} titleAlign="center" question={questions[currentPage]} answers={answers} questionId={QUESTION_IDS[currentPage]} onAnswerChange={onAnswerChange} />
        </QuestionList>}
        {currentPage === 10 && <QuestionList ref={htmlElRef} tabIndex={0}>
            <AlternateMultipleChoiceQuestion checkedItem={answered[currentPage]} refProp={questionRefs[currentPage]} titleAlign="center" question="Please describe your physical health (problems with pain, mobility, difficulties caring for yourself or feeling physically unwell) over the last week" answers={healthAnswers} questionId={93} onAnswerChange={onAnswerChange} />
        </QuestionList>}
        <ButtonContainer style={{ justifyContent: "center", margin: "20px 0", flexWrap: "wrap" }}>
            {loading && <LoadingIndicator />}
            {!loading && <Button text="Previous" onClick={goToPreviousPage} />}
            {!loading && <Button text="Next" onClick={goToNextPage} />}
            {!loading && <Button text="Submit answers" onClick={onSubmit} onDisabledClick={onDisabledClick} disabled={!checkEnoughAnswered()} />}
        </ButtonContainer>
        <ProgressBar length={11} progress={numberAnswered} />
        <ErrorToast role="alert" aria-atomic="true" className={error ? "sliding" : "hidden"}>{error}</ErrorToast>
        <IncompleteToast className={incomplete ? "sliding" : "hidden"}>
            You have not completed one or more of the questions.  Are you sure you want to submit this outcome measure?
            <ToastButtonContainer>
                <Button text="Cancel" onClick={cancelSubmit} />
                <Button text="Save" onClick={goToNextPageIfComplete} />
            </ToastButtonContainer>
        </IncompleteToast>
        <AttributionContainer>
            ReQoL&trade; Version 1.1 &copy; Copyright, The University of Sheffield 2016, 2018. All Rights Reserved.<br />
            The authors have asserted their moral rights.<br />
            Oxford University Innovation Limited is exclusively licensed to grant permissions to use the ReQoL&trade;.
        </AttributionContainer>
    </ContentWrapper >;
};

export default ReqolPage;



// WEBPACK FOOTER //
// ./src/Forms/reqol/ReqolPage.tsx